import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToHash } from "@/App";

const navKeys = ["home", "locale", "menu", "servizi", "gallery", "contatti"];

const Header = () => {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onDark = location.pathname === "/" && !scrolled;

  const items = [
    { label: t.nav.home, hash: null },
    { label: t.nav.locale, hash: "#locale" },
    { label: t.nav.menu, path: "/menu" },
    { label: t.nav.servizi, path: "/servizi" },
    { label: t.nav.gallery, hash: "#gallery" },
    { label: t.nav.contatti, hash: "#contatti" },
  ];

  const go = (item) => {
    setOpen(false);
    if (item.path) {
      navigate(item.path);
      window.__lenis?.scrollTo(0, { immediate: true });
      return;
    }
    if (!item.hash) {
      if (location.pathname !== "/") navigate("/", { state: { scrollTo: "top" } });
      else window.__lenis?.scrollTo(0);
      return;
    }
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: item.hash } });
    else scrollToHash(item.hash);
  };

  const textMain = onDark ? "text-ivory" : "text-anthracite";
  const textSoft = onDark ? "text-ivory/70 hover:text-ivory" : "text-anthracite/70 hover:text-bordeaux";
  const textDim = onDark ? "text-ivory/45 hover:text-ivory" : "text-anthracite/45 hover:text-anthracite";
  const accent = onDark ? "text-ivory" : "text-bordeaux";

  return (
    <>
      <header
        data-testid="site-header"
        data-theme={onDark ? "dark" : "light"}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          onDark
            ? "bg-transparent border-b border-transparent"
            : "bg-ivory/90 backdrop-blur-md border-b border-sand/60 shadow-[0_1px_24px_rgba(39,37,34,0.06)]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] md:h-24 flex items-center justify-between gap-4">
          <button
            onClick={() => go({ hash: null })}
            className="flex items-center"
            data-testid="header-logo"
            aria-label="U Magazin — home"
          >
            <img
              src={onDark ? "/images/logo-white.png" : "/images/logo-dark.png"}
              alt="U Magazin"
              className="h-11 md:h-16 w-auto transition-opacity duration-500"
              width="1888"
              height="620"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-9" aria-label="Principale">
            {items.map((item, i) => (
              <button
                key={i}
                onClick={() => go(item)}
                data-testid={`nav-${navKeys[i]}`}
                className={`group relative text-[13px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                  item.path && location.pathname === item.path ? accent : textSoft
                }`}
              >
                {item.label}
                <span
                  className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-current transition-transform duration-500 ease-out group-hover:scale-x-100"
                  aria-hidden="true"
                />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-6">
            <div
              className="flex items-center text-[11px] tracking-[0.2em] font-medium"
              data-testid="lang-switch"
            >
              <button
                onClick={() => setLang("it")}
                data-testid="lang-switch-it"
                className={`px-1.5 py-1 transition-colors duration-300 ${
                  lang === "it" ? accent : textDim
                }`}
              >
                IT
              </button>
              <span className={onDark ? "text-ivory/30" : "text-anthracite/25"}>/</span>
              <button
                onClick={() => setLang("en")}
                data-testid="lang-switch-en"
                className={`px-1.5 py-1 transition-colors duration-300 ${
                  lang === "en" ? accent : textDim
                }`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => go({ hash: "#contatti" })}
              data-testid="header-book-button"
              className={`hidden md:inline-flex ${onDark ? "btn-light" : "btn-primary"} !px-6 !py-2.5 !text-xs`}
            >
              {t.nav.book}
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
              className={`lg:hidden p-2 ${textMain}`}
              aria-label="Apri il menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] bg-bordeaux text-ivory flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-[72px]">
              <img src="/images/logo-white.png" alt="U Magazin" className="h-11 w-auto" />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                className="p-2"
                aria-label="Chiudi il menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1" aria-label="Mobile">
              {items.map((item, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 + 0.07 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(item)}
                  data-testid={`mobile-nav-${navKeys[i]}`}
                  className="flex items-baseline gap-4 text-left py-3 hover:text-sand transition-colors duration-300"
                >
                  <span className="font-serif italic text-sm text-sand/60">0{i + 1}</span>
                  <span className="font-serif text-4xl tracking-tight">{item.label}</span>
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10 flex items-end justify-between gap-6">
              <p className="text-sm text-ivory/55 max-w-[240px] leading-relaxed">{t.footer.tagline}</p>
              <button
                onClick={() => go({ hash: "#contatti" })}
                data-testid="mobile-menu-book-button"
                className="btn-light !px-6 !py-2.5 !text-xs"
              >
                {t.nav.book}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
